import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import { SafeAreaView, View, Text, StyleSheet, Pressable } from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import {
  WeekConfiguration,
  createEmptyWeek,
  generatePeakObj,
} from '../components/common/utils/peak-off-peak-field';
import DropdownComponentPeak from '../components/DropdownComponentPeak';
import { notificationService } from '../services/notification-service';
import { Dropdown } from 'react-native-element-dropdown';

type TimeErrors = {
  [day: string]: {
    start?: string;
    end?: string;
  };
};

type PresetOption = 'weekdays' | 'weekends' | 'all';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const WEEKENDS = ['Saturday', 'Sunday'];
const ALL_DAYS = [...WEEKDAYS, ...WEEKENDS];

const PRESET_OPTIONS = [
  { value: 'weekdays', label: 'Apply to Weekdays' },
  { value: 'weekends', label: 'Apply to Weekends' },
  { value: 'all', label: 'Apply to All Days' },
];

const RegisterChargerCheckbox = (props: {
  setPeakSelection: (peakSelection: WeekConfiguration) => void;
  checkboxSelected: {
    updatedDay: string[];
    startEndTime: {}[];
  };
  generatePeakObjCheckbox: WeekConfiguration;
  editChargerStatus: boolean;
  selectionErrors: (isError: boolean) => void;
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [weekView, setWeekView] = useState<WeekConfiguration>(() =>
    createEmptyWeek()
  );
  const [errors, setErrors] = useState<TimeErrors>({}); // Error state
  const didSeed = useRef(false);
  const defaultWeekView = generatePeakObj(
    '0000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000'
  );

  // Quick apply preset state
  const [presetFrom, setPresetFrom] = useState<number>(16); // Default 4 PM
  const [presetTo, setPresetTo] = useState<number>(21); // Default 9 PM
  const [presetType, setPresetType] = useState<PresetOption>('weekdays');
  const [isPresetExpanded, setIsPresetExpanded] = useState<boolean>(false);

  // Handle applying preset to selected days
  const handleApplyPreset = () => {
    if (presetTo <= presetFrom) {
      notificationService.sendNotification(
        'error',
        'End time must be after start time'
      );
      return;
    }

    const targetDays =
      presetType === 'weekdays'
        ? WEEKDAYS
        : presetType === 'weekends'
        ? WEEKENDS
        : ALL_DAYS;

    // Build new week configuration
    const newWeek = { ...weekView };
    targetDays.forEach((day) => {
      newWeek[day] = { start: presetFrom, end: presetTo };
    });

    // Update selected options (check the checkboxes)
    const newSelectedOptions = [...new Set([...selectedOptions, ...targetDays])];

    setWeekView(newWeek);
    setSelectedOptions(newSelectedOptions);
    props.setPeakSelection(newWeek);

    // Clear any errors for the updated days
    const clearedErrors = { ...errors };
    targetDays.forEach((day) => {
      clearedErrors[day] = { start: undefined, end: undefined };
    });
    setErrors(clearedErrors);

    notificationService.sendNotification(
      'success',
      `Peak hours applied to ${presetType === 'all' ? 'all days' : presetType}`
    );
  };

  const handleOptionSelect = (day: string) => {
    const index = selectedOptions.indexOf(day);
    const isSelected = selectedOptions.includes(day);
    const nextSelected = isSelected
      ? selectedOptions.filter((d) => d !== day)
      : [...selectedOptions, day];

    if (index === -1) {
      setSelectedOptions([...selectedOptions, day]);
    } else {
      const updatedOptions = [...selectedOptions];
      updatedOptions.splice(index, 1);
      setSelectedOptions(updatedOptions);
    }

    const newWeek = {
      ...weekView,
      [day]: {
        start: 0,
        end: 0,
      },
    };

    if (day && index !== -1) {
      setWeekView(newWeek);
      props.setPeakSelection(newWeek);
      setSelectedOptions(nextSelected);
    }
  };

  useEffect(() => {
    if (
      props.editChargerStatus &&
      !didSeed.current &&
      JSON.stringify(props.generatePeakObjCheckbox) !==
        JSON.stringify(defaultWeekView)
    ) {
      setWeekView(props.generatePeakObjCheckbox);
      const initialChecked = Object.entries(props.generatePeakObjCheckbox)
        .filter(([_, cfg]) => cfg.start !== 0 || cfg.end !== 0)
        .map(([day]) => day);
      setSelectedOptions(initialChecked);
      didSeed.current = true;
    }
  }, [props.editChargerStatus, props.generatePeakObjCheckbox]);

  const handleTimeSelect = (sel: any, day: string, type: 'start' | 'end') => {
    const newTime = sel.value;
    const currentDayConfig = weekView[day];

    const newConfig = {
      start: type === 'start' ? newTime : currentDayConfig.start,
      end: type === 'end' ? newTime : currentDayConfig.end,
    };

    const newWeek = {
      ...weekView,
      [day]: newConfig,
    };

    // Validation: Check if end is before start
    if (newConfig.end < newConfig.start) {
      notificationService.sendNotification(
        'error',
        'End time cannot be before start time'
      );
      setErrors((prevErrors) => ({
        ...prevErrors,
        [day]: {
          ...prevErrors[day], // Preserve existing errors for this day
          [type]: 'End time cannot be before start time',
        },
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [day]: {
          ...prevErrors[day],
          start: undefined, // Clear the error for this type
          end: undefined,
        },
      }));
      props.setPeakSelection(newWeek);
    }

    setWeekView(newWeek);
  };

  useEffect(() => {
    const hasErrors = Object.values(errors).some((dayErrors) =>
      Object.values(dayErrors || {}).some((error) => error !== undefined)
    );

    props.selectionErrors(hasErrors);
  }, [errors]);

  const options = [
    { value: 0, label: '12 AM' },
    { value: 1, label: '1 AM' },
    { value: 2, label: '2 AM' },
    { value: 3, label: '3 AM' },
    { value: 4, label: '4 AM' },
    { value: 5, label: '5 AM' },
    { value: 6, label: '6 AM' },
    { value: 7, label: '7 AM' },
    { value: 8, label: '8 AM' },
    { value: 9, label: '9 AM' },
    { value: 10, label: '10 AM' },
    { value: 11, label: '11 AM' },
    { value: 12, label: '12 PM' },
    { value: 13, label: '1 PM' },
    { value: 14, label: '2 PM' },
    { value: 15, label: '3 PM' },
    { value: 16, label: '4 PM' },
    { value: 17, label: '5 PM' },
    { value: 18, label: '6 PM' },
    { value: 19, label: '7 PM' },
    { value: 20, label: '8 PM' },
    { value: 21, label: '9 PM' },
    { value: 22, label: '10 PM' },
    { value: 23, label: '11 PM' },
    { value: 24, label: '12 AM (Midnight)' },
  ];

  const startStopSelection = Object.entries(weekView).map(
    ([day, config], index) => {
      const dayErrors = errors[day] || {};
      return (
        <View key={`${day}StartStop`} style={styles.row} className="mt-2">
          <View key={day} style={styles.row} className="w-32 h-10">
            <Pressable onPress={() => handleOptionSelect(day)}>
              <View style={styles.checkbox}>
                {selectedOptions.find((d) => d === day) ? (
                  <View>
                    <FontAwesome6 name="check" size={17} color="white" />
                  </View>
                ) : (
                  <View></View>
                )}
              </View>
            </Pressable>
            <Text className="text-base text-white">{day}</Text>
          </View>

          <DropdownComponentPeak
            key={`${day}Start`}
            data={options}
            placeholderText={'start time'}
            onItemSelected={(s) => handleTimeSelect(s, `${day}`, 'start')}
            initVal={options[config.start].value}
            hidden={selectedOptions.find((d) => d === day) ? false : true}
            error={!!dayErrors.start} // Error for start
            type="start"
          />

          <DropdownComponentPeak
            key={`${day}Stop`}
            data={options}
            placeholderText={'end time'}
            onItemSelected={(value) => handleTimeSelect(value, `${day}`, 'end')}
            initVal={options[config.end].value}
            hidden={selectedOptions.find((d) => d === day) ? false : true}
            error={!!dayErrors.end} // Error for end
            type="end"
          />
        </View>
      );
    }
  );

  return (
    <View>
      {/* Separator */}
      <View style={styles.separator} />

      {/* Quick Apply Preset Section (Collapsible) */}
      <View style={styles.presetContainer}>
        <Pressable
          style={styles.presetHeader}
          onPress={() => setIsPresetExpanded(!isPresetExpanded)}
        >
          <Text style={styles.presetTitle}>Quick Apply Hours</Text>
          <FontAwesome6
            name={isPresetExpanded ? 'chevron-down' : 'chevron-right'}
            size={14}
            color="#51B868"
          />
        </Pressable>

        {isPresetExpanded && (
          <>
            <View style={[styles.presetRow, { marginTop: 12 }]}>
              <View style={styles.presetDropdownWrapper}>
                <Text style={styles.presetLabel}>From</Text>
                <Dropdown
                  style={styles.presetDropdown}
                  selectedTextStyle={styles.presetDropdownText}
                  placeholderStyle={styles.presetDropdownText}
                  data={options.slice(0, 24)} // Exclude midnight end option for "From"
                  labelField="label"
                  valueField="value"
                  value={presetFrom}
                  onChange={(item) => setPresetFrom(item.value)}
                  maxHeight={200}
                />
              </View>
              <View style={styles.presetDropdownWrapper}>
                <Text style={styles.presetLabel}>To</Text>
                <Dropdown
                  style={styles.presetDropdown}
                  selectedTextStyle={styles.presetDropdownText}
                  placeholderStyle={styles.presetDropdownText}
                  data={options.slice(1)} // Exclude 12 AM start option for "To"
                  labelField="label"
                  valueField="value"
                  value={presetTo}
                  onChange={(item) => setPresetTo(item.value)}
                  maxHeight={200}
                />
              </View>
            </View>
            <View style={[styles.presetRow, { marginBottom: 0 }]}>
              <Dropdown
                style={styles.presetTypeDropdown}
                selectedTextStyle={styles.presetDropdownText}
                placeholderStyle={styles.presetDropdownText}
                data={PRESET_OPTIONS}
                labelField="label"
                valueField="value"
                value={presetType}
                onChange={(item) => setPresetType(item.value as PresetOption)}
                maxHeight={150}
              />
              <Pressable style={styles.applyButton} onPress={handleApplyPreset}>
                <Text style={styles.applyButtonText}>Apply</Text>
              </Pressable>
            </View>
          </>
        )}
      </View>

      {/* Day-by-Day Selection */}
      <View className="flex flex-row">
        <View>{startStopSelection}</View>
      </View>
    </View>
  );
};

export default RegisterChargerCheckbox;

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B1E1D',
  },
  containerBgColor: {
    backgroundColor: '#0B1E1D',
  },
  BgColor1: {
    backgroundColor: '#292D33',
  },
  BgColor2: {
    backgroundColor: '#234D4E',
  },
  line: {
    borderBottomColor: '#636363',
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    marginVertical: 20,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 1,
    borderColor: 'white',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
    borderRadius: 5,
  },
  // Preset section styles
  presetContainer: {
    backgroundColor: '#1A2E2D',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  presetHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  presetTitle: {
    color: '#51B868',
    fontSize: 14,
    fontWeight: '600',
  },
  presetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  presetLabel: {
    color: '#9CA3AF',
    fontSize: 12,
    marginBottom: 4,
  },
  presetDropdownWrapper: {
    flex: 1,
  },
  presetDropdown: {
    backgroundColor: '#234D4E',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    minHeight: 40,
  },
  presetTypeDropdown: {
    flex: 1,
    backgroundColor: '#234D4E',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    minHeight: 40,
  },
  presetDropdownText: {
    color: 'white',
    fontSize: 14,
  },
  applyButton: {
    backgroundColor: '#51B868',
    borderRadius: 6,
    paddingHorizontal: 20,
    paddingVertical: 10,
    minHeight: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  applyButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  separator: {
    height: 1,
    backgroundColor: '#3A4A49',
    marginVertical: 12,
  },
});
